import { create } from 'zustand';
import { Tier, PreviewMode, getAccess, startPreview } from '../services/api';
import { WebSocketManager, CloseCode, MetaMessage } from '../services/websocket';
import { trackEvent, trackEventOnce } from '../services/analytics';

interface MarketAnalysis {
  summary: string;
  sentiment: 'bullish' | 'bearish' | 'neutral';
  confidence: number;
  timestamp: string;
}

interface AnalysisMeta {
  cadenceSeconds: number;
  lastRunAt: string;
  nextRunAt: string;
  status: string;
  lastDurationMs: number;
  lastError: string | null;
}

interface ReachabilityLevel {
  movePct: number;
  probPct: number;
}

interface Reachability {
  horizonMinutes: number;
  levels: ReachabilityLevel[];
}

interface TradeStream {
  lastPrice: number;
  EMA: number;
  slope: number;
  bigTrendSlope: number;
  trendDirection: 'UP' | 'DOWN';
  intraDayTrendDirection: 'UP' | 'DOWN';
  longPressure: number;
  shortPressure: number;
  volumeSpeed: number;
  orderBookSkew: number;
  depthImbalance: number;
  bidWallDetected: boolean;
  askWallDetected: boolean;
  tickMomentum: number;
  deBuffon: number;
  acceleration: number;
  stdDev: number;
  position: number;
  reachability: Reachability | null;
  marketAnalysis: MarketAnalysis | null;
  aiAnalysis: string | null;
  analysisMeta: AnalysisMeta | null;
  lastByteActivity: string[];
  cache: string;
  [key: string]: unknown;
}

interface TickTally {
  longPressure: number;
  shortPressure: number;
}

interface Node {
  id: string;
  group: string;
}

interface Link {
  source: string;
  target: string;
  value: number;
}

interface AutomataState {
  nodes: Node[];
  links: Link[];
}

interface CellularEntry {
  rule: number;
  value: number;
}

type AppState = 'loading' | 'preview' | 'trial' | 'active' | 'blocked';
type CloseReason = 'preview_expired' | 'trial_expired' | 'paywall' | 'unauthorized' | null;
type BlockedReason = 'new' | 'preview_expired' | null;

interface StoreState {
  // Access state
  appState: AppState;
  tier: Tier;
  serverNow: string | null;
  expiresAt: string | null;
  previewMode: PreviewMode | null;
  closeReason: CloseReason;
  blockedReason: BlockedReason;
  canStartPreview: boolean;
  hasInitialSnapshot: boolean;

  // Connection state
  isConnected: boolean;
  wsManager: WebSocketManager | null;

  // Data state
  dataFeed: Partial<TradeStream>;
  aiAnalysis: string | null;
  analysisMeta: AnalysisMeta | null;
  tickSequenceBuffer: string;
  automataState: AutomataState;
  fourBitSeq: string[];
  fourBitState: Record<string, number>;
  cellularSeq: CellularEntry[];
  cellularState: Record<number, number>;
  tickTally: TickTally;

  // Actions
  initialize: () => Promise<void>;
  beginPreview: () => Promise<void>;
  updateDataFeed: (data: TradeStream) => void;
  setAppState: (state: AppState) => void;
  handleCloseReason: (reason: CloseReason) => void;
  connectWebSocket: () => Promise<void>;
  disconnect: () => void;
}

const createInitialNodes = (): Node[] => {
  return Array.from(Array(2 ** 4)).map((_, i) => ({
    id: i.toString(2).padStart(4, '0'),
    group: i.toString(),
  }));
};

export const useStore = create<StoreState>((set, get) => ({
  // Initial state
  appState: 'loading',
  tier: 'blocked',
  serverNow: null,
  expiresAt: null,
  previewMode: null,
  closeReason: null,
  blockedReason: null,
  canStartPreview: false,
  hasInitialSnapshot: false,

  isConnected: false,
  wsManager: null,

  dataFeed: {},
  aiAnalysis: null,
  analysisMeta: null,
  tickSequenceBuffer: '',
  automataState: {
    nodes: createInitialNodes(),
    links: [],
  },
  fourBitSeq: [],
  fourBitState: {},
  cellularSeq: [],
  cellularState: {},
  tickTally: {
    longPressure: 0,
    shortPressure: 0,
  },

  // Actions
  initialize: async () => {
    trackEvent('access_initialize_attempt');
    try {
      const access = await getAccess();

      if (access.tier === 'blocked') {
        if (access.blocked_reason === 'new') {
          trackEvent('access_state_blocked_new');
        } else if (access.blocked_reason === 'preview_expired') {
          trackEvent('access_state_blocked_preview_expired');
        } else {
          trackEvent('access_state_blocked');
        }
        set({
          tier: 'blocked',
          serverNow: access.server_now,
          expiresAt: null,
          previewMode: null,
          appState: 'blocked',
          closeReason: null,
          blockedReason: access.blocked_reason ?? null,
          canStartPreview: access.blocked_reason === 'new',
          hasInitialSnapshot: false,
        });
        return;
      }

      // Determine expires_at based on tier
      let expiresAt: string | null = null;
      if (access.tier === 'preview') {
        expiresAt = access.preview_expires_at;
      } else if (access.tier === 'trial') {
        expiresAt = access.trial_expires_at;
      } else if (access.tier === 'active') {
        expiresAt = access.current_period_end;
      }

      set({
        tier: access.tier,
        serverNow: access.server_now,
        expiresAt,
        previewMode: access.preview_mode,
        appState: access.tier as AppState,
        closeReason: null,
        blockedReason: null,
        canStartPreview: false,
        hasInitialSnapshot: false,
      });

      if (access.tier === 'preview') {
        trackEvent('access_state_preview');
      } else if (access.tier === 'trial') {
        trackEvent('access_state_trial');
      } else if (access.tier === 'active') {
        trackEvent('access_state_active');
      }

      await get().connectWebSocket();
    } catch (error) {
      console.error('Failed to initialize:', error);
      trackEvent('access_initialize_error');
      set({
        appState: 'blocked',
        tier: 'blocked',
        blockedReason: null,
        hasInitialSnapshot: false,
      });
    }
  },

  beginPreview: async () => {
    trackEvent('preview_start_attempt');
    try {
      const preview = await startPreview();

      set({
        tier: 'preview',
        serverNow: preview.server_now,
        expiresAt: preview.preview_expires_at,
        previewMode: preview.preview_mode,
        appState: 'preview',
        closeReason: null,
        blockedReason: null,
        canStartPreview: false,
        hasInitialSnapshot: false,
      });

      trackEvent('preview_start_success');

      await get().connectWebSocket();
    } catch (error) {
      console.error('Failed to start preview:', error);
      trackEvent('preview_start_error');
      set({
        appState: 'blocked',
        tier: 'blocked',
        blockedReason: null,
        canStartPreview: false,
        hasInitialSnapshot: false,
      });
    }
  },

  updateDataFeed: (data: TradeStream) => {
    const state = get();

    // Handle case where lastByteActivity might not exist
    if (!data.lastByteActivity || !Array.isArray(data.lastByteActivity)) {
      if (!state.hasInitialSnapshot) {
        trackEventOnce('stream_first_snapshot');
      }
      set({
        dataFeed: data,
        aiAnalysis: data.aiAnalysis ?? null,
        analysisMeta: data.analysisMeta ?? null,
        tickSequenceBuffer: data.cache || '',
        hasInitialSnapshot: true,
        tickTally: {
          longPressure: data.longPressure || 0,
          shortPressure: data.shortPressure || 0,
        },
      });
      return;
    }

    const eightBitArray = data.lastByteActivity.map((b) => (b.includes('+') ? 1 : 0));
    const rule = parseInt(eightBitArray.join(''), 2);
    const cellularSeq = [{ rule, value: 0 }, ...state.cellularSeq.slice(0, 100)];

    const cellularState = cellularSeq.reduce((acc, c) => {
      acc[c.rule] = (acc[c.rule] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);

    const half = Math.ceil(eightBitArray.length / 2);
    const firstHalf = eightBitArray.slice(0, half).join('');
    const secondHalf = eightBitArray.slice(-half).join('');
    const fourBitSeq = [firstHalf, secondHalf, ...state.fourBitSeq.slice(0, 1000)];

    const fourBitState = fourBitSeq.reduce((acc, current, i) => {
      const previous = fourBitSeq[i + 1];
      if (!previous) return acc;
      const key = `${previous}${current}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const someLinks = state.automataState.nodes.flatMap((node) => {
      return Array.from(Array(2 ** 4))
        .map((_, ii) => {
          const target = ii.toString(2).padStart(4, '0');
          const value = fourBitState[`${node.id}${target}`];
          return value ? { source: node.id, target, value } : null;
        })
        .filter((link): link is Link => link !== null);
    });

    set({
      dataFeed: data,
      aiAnalysis: data.aiAnalysis ?? null,
      analysisMeta: data.analysisMeta ?? null,
      tickSequenceBuffer: data.cache || '',
      hasInitialSnapshot: true,
      tickTally: {
        longPressure: data.longPressure || 0,
        shortPressure: data.shortPressure || 0,
      },
      fourBitSeq,
      cellularSeq,
      cellularState,
      fourBitState,
      automataState: {
        nodes: state.automataState.nodes,
        links: someLinks,
      },
    });

    if (!state.hasInitialSnapshot) {
      trackEventOnce('stream_first_snapshot');
    }
  },

  setAppState: (appState: AppState) => {
    set({ appState });
  },

  handleCloseReason: (reason: CloseReason) => {
    if (reason === 'preview_expired') {
      trackEvent('stream_closed_preview_expired');
    } else if (reason === 'trial_expired') {
      trackEvent('stream_closed_trial_expired');
    } else if (reason === 'paywall') {
      trackEvent('stream_closed_paywall');
    } else if (reason === 'unauthorized') {
      trackEvent('stream_closed_unauthorized');
    }

    set({
      closeReason: reason,
      blockedReason: null,
      appState: 'blocked',
      tier: 'blocked',
      hasInitialSnapshot: false,
    });
  },

  connectWebSocket: async () => {
    trackEvent('ws_connect_attempt');

    // Disconnect existing connection if any
    const { wsManager: existing } = get();
    if (existing) {
      existing.disconnect();
    }

    const wsManager = new WebSocketManager({
      onMeta: (msg: MetaMessage) => {
        const previousTier = get().tier;
        if (previousTier !== msg.tier) {
          trackEvent(`ws_meta_tier_${msg.tier}`);
        }
        set({
          tier: msg.tier,
          expiresAt: msg.expires_at,
          serverNow: msg.server_now,
          appState: msg.tier as AppState,
        });
      },
      onTick: (data) => {
        get().updateDataFeed(data as TradeStream);
      },
      onClose: (code, reason) => {
        console.log('WebSocket closed:', code, reason);

        switch (code) {
          case CloseCode.PREVIEW_EXPIRED:
            get().handleCloseReason('preview_expired');
            break;
          case CloseCode.TRIAL_EXPIRED:
            get().handleCloseReason('trial_expired');
            break;
          case CloseCode.PAYWALL:
            get().handleCloseReason('paywall');
            break;
          case CloseCode.UNAUTHORIZED:
            get().handleCloseReason('unauthorized');
            break;
          case CloseCode.SERVER_ERROR:
            trackEvent('ws_closed_server_error');
            break;
          default:
            if (code !== 1000) {
              trackEvent('ws_closed_unexpected');
            }
            break;
        }
      },
      onConnected: () => {
        console.log('Connected to Galois AI server');
        trackEvent('ws_connected');
        set({ isConnected: true });
      },
      onDisconnected: () => {
        console.log('Disconnected from server');
        if (get().isConnected) {
          trackEvent('ws_disconnected');
        }
        set({ isConnected: false });
      },
    });

    set({ wsManager });
    const connected = await wsManager.connect();
    if (!connected) {
      trackEvent('ws_connect_unauthorized');
    }
  },

  disconnect: () => {
    const { wsManager } = get();
    if (wsManager) {
      trackEvent('ws_disconnect_manual');
      wsManager.disconnect();
      set({ wsManager: null, isConnected: false, hasInitialSnapshot: false });
    }
  },
}));
